import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import { ChevronDown, ChevronUp, FileText, BookOpen, Send, Baby, Heart, MessageCircle, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../utils/api';
import PageMeta from '../components/PageMeta';

const Resources = () => {
  const { language } = useLanguage();
  const [openFaq, setOpenFaq] = useState(null);
  const [formType, setFormType] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [baptismData, setBaptismData] = useState({
    child_name: '',
    date_of_birth: '',
    gender: '',
    father_name: '',
    mother_name: '',
    contact_email: '',
    contact_phone: '',
    preferred_date: '',
    additional_notes: ''
  });

  const [weddingData, setWeddingData] = useState({
    groom_name: '',
    bride_name: '',
    contact_email: '',
    contact_phone: '',
    preferred_date: '',
    additional_notes: ''
  });

  const [counselingData, setCounselingData] = useState({
    full_name: '',
    email: '',
    phone: '',
    counseling_type: '',
    message: ''
  });

  const [membershipData, setMembershipData] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    reason: ''
  });

  const faqs = [
    {
      id: 1,
      question_en: 'What are the service times?',
      question_ti: 'ናይ መድባት ግዜ  መዓስ እዩ?',
      answer_en: 'Sunday Divine Liturgy starts at 7:00 AM. Sunday School (Youth & Children) at 10:30 AM. Women\'s Ministry at 11:30 AM. All services are every Sunday.',
      answer_ti: 'ሰንበት ቅዳሴ ብሰዓት 7:00 ንጉሆ ይጅምር። ሰንበት ትምህርቲ (መንእሰያትን ህፃናትን) ብሰዓት 10:30። ደቂ ኣንስትዮ አገልግሎት ብሰዓት 11:30። ኩሎም አገልግሎታት ኩሉ ሰንበት እዮም።'
    },
    {
      id: 2,
      question_en: 'How can I request baptism for my child?',
      question_ti: 'ንውላደይ ጥምቀት ከመይ ገይረ ክሓትት?',
      answer_en: 'Please fill out the Baptism Request Form below or contact us directly at mheeotcdallas@gmail.com. Our clergy will guide you through the preparation process.',
      answer_ti: 'በታሕቲ ዘሎ ጥምቀት ሕቶ ቅጽፈ መልእ ወይ ብቀጥታ ርኸቡና ብ mheeotcdallas@gmail.com። ካህናትና ብመስርሕ ድልውነት ክመርሑኹም እዮም።'
    },
    {
      id: 3,
      question_en: 'What do I need for a wedding?',
      question_ti: 'ንመርዓው እንታይ የድልየኒ?',
      answer_en: 'Please let us know at least 6 months in advance so you can attend premarital counseling classes. Submit the Wedding Request Form below and we will contact you with detailed requirements.',
      answer_ti: 'ቅድሚ መርዓ ምኽሪ ክፍሊ ንምክታል ኣብቐድመ ቅድሚ 6 ወርሒ ሕበሩና። በታሕቲ ዘሎ መርዓው ሕቶ ቅጽፈ መልእ፡ ብዝርዝር ክንሕብር ኢና።'
    },
    {
      id: 4,
      question_en: 'Do you offer religious education in English?',
      question_ti: 'ብእንግሊዝኛ ሃይማኖታዊ ትምህርቲ ትህቡ ዶ?',
      answer_en: 'Yes! We offer Sunday school and religious education programs in English, Tigrinya, and Geez for all age groups.',
      answer_ti: 'እወ! ሰንበት ትምህርቲን ሃይማኖታዊ ትምህርቲን ብእንግሊዝኛ፡ ትግርኛን ግእዝን ንኩሉ ዕድመ ንህብ።'
    },
    {
      id: 5,
      question_en: 'How can I support the church building campaign?',
      question_ti: 'ንህንፃ ቤተ ክርስትያን ዘመተ ከመይ ገይረ ከደግፍ?',
      answer_en: 'You can donate through our website, Zelle (mheeotcdallas@gmail.com), or by check. All donations are tax-deductible. Visit our Donate page for more information.',
      answer_ti: 'ብመርበብና፡ Zelle (mheeotcdallas@gmail.com)፡ ወይ ብቼክ ክትልግስ ትኽእል። ኩሉ ልግሲ ግብሪ የዕርፍ እዩ። ንዝያዳ መረዳእታ ናብ ሃበ ገጽ ጐብጻ።'
    }
  ];

  const toggleFaq = (id) => {
    setOpenFaq(openFaq === id ? null : id);
  };

  const handleBaptismSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/forms/baptism', baptismData);
      toast.success(t(language, 'resources.baptismSuccess'));
      setBaptismData({
        child_name: '',
        date_of_birth: '',
        gender: '',
        father_name: '',
        mother_name: '',
        contact_email: '',
        contact_phone: '',
        preferred_date: '',
        additional_notes: ''
      });
      setFormType(null);
    } catch (error) {
      toast.error(t(language, 'resources.submitError'));
    } finally {
      setLoading(false);
    }
  };

  const handleWeddingSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/forms/wedding', weddingData);
      toast.success(t(language, 'resources.weddingSuccess'));
      setWeddingData({
        groom_name: '',
        bride_name: '',
        contact_email: '',
        contact_phone: '',
        preferred_date: '',
        additional_notes: ''
      });
      setFormType(null);
    } catch (error) {
      toast.error(t(language, 'resources.submitError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCounselingSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/forms/counseling', counselingData);
      toast.success(t(language, 'resources.counselingSuccess'));
      setCounselingData({
        full_name: '',
        email: '',
        phone: '',
        counseling_type: '',
        message: ''
      });
      setFormType(null);
    } catch (error) {
      toast.error(t(language, 'resources.submitError'));
    } finally {
      setLoading(false);
    }
  };

  const handleMembershipSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/forms/membership', membershipData);
      toast.success(t(language, 'resources.membershipSuccess'));
      setMembershipData({
        full_name: '',
        email: '',
        phone: '',
        address: '',
        reason: ''
      });
      setFormType(null);
    } catch (error) {
      toast.error(t(language, 'resources.submitError'));
    } finally {
      setLoading(false);
    }
  };

  return (


    <>


      <PageMeta 


        title="Resources"


        path="/resources"


        description="Church forms and resources including baptism, wedding, counseling, and membership applications."


      />
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-gradient-to-br from-blue-100 via-purple-50 to-pink-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <BookOpen className="w-16 h-16 text-purple-600 dark:text-purple-500 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
            {t(language, 'resources.title')}
          </h1>
          <p className="text-lg text-gray-700 dark:text-gray-300">
            {t(language, 'resources.subtitle')}
          </p>
        </div>
      </section>

      {/* Request Forms */}
      <section className="py-16 bg-purple-50 dark:bg-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12" style={{fontFamily: 'Playfair Display, serif'}}>
            {t(language, 'resources.requestForms')}
          </h2>

          {!formType ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Baptism Form */}
              <Card className="p-8 text-center hover:shadow-xl transition-shadow cursor-pointer" onClick={() => setFormType('baptism')}>
                <Baby className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {t(language, 'resources.baptismRequest')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {t(language, 'resources.baptismDescription')}
                </p>
              </Card>

              {/* Wedding Form */}
              <Card className="p-8 text-center hover:shadow-xl transition-shadow cursor-pointer" onClick={() => setFormType('wedding')}>
                <Heart className="w-16 h-16 text-pink-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {t(language, 'resources.weddingRequest')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {t(language, 'resources.weddingDescription')}
                </p>
              </Card>

              {/* Counseling Form */}
              <Card className="p-8 text-center hover:shadow-xl transition-shadow cursor-pointer" onClick={() => setFormType('counseling')}>
                <MessageCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {t(language, 'resources.counselingRequest')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {t(language, 'resources.counselingDescription')}
                </p>
              </Card>

              {/* Membership Form */}
              <Card className="p-8 text-center hover:shadow-xl transition-shadow cursor-pointer" onClick={() => setFormType('membership')}>
                <Users className="w-16 h-16 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {t(language, 'resources.membershipRequest')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {t(language, 'resources.membershipDescription')}
                </p>
              </Card>
            </div>
          ) : formType === 'baptism' ? (
            <Card className="p-8 max-w-3xl mx-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {t(language, 'resources.baptismFormTitle')}
                </h3>
                <Button variant="outline" onClick={() => setFormType(null)}>
                  {t(language, 'resources.back')}
                </Button>
              </div>

              <form onSubmit={handleBaptismSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                    {t(language, 'resources.childName')}
                  </label>
                  <Input
                    type="text"
                    value={baptismData.child_name}
                    onChange={(e) => setBaptismData({...baptismData, child_name: e.target.value})}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:bg-gray-700 dark:text-white transition-all"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      {t(language, 'resources.dateOfBirth')}
                    </label>
                    <Input
                      type="date"
                      value={baptismData.date_of_birth}
                      onChange={(e) => setBaptismData({...baptismData, date_of_birth: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:bg-gray-700 dark:text-white transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      {t(language, 'resources.gender')}
                    </label>
                    <select
                      className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:bg-gray-700 dark:text-white transition-all"
                      value={baptismData.gender}
                      onChange={(e) => setBaptismData({...baptismData, gender: e.target.value})}
                      required
                    >
                      <option value="">{t(language, 'resources.selectGender')}</option>
                      <option value="male">{t(language, 'resources.male')}</option>
                      <option value="female">{t(language, 'resources.female')}</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      {t(language, 'resources.fatherName')}
                    </label>
                    <Input
                      type="text"
                      value={baptismData.father_name}
                      onChange={(e) => setBaptismData({...baptismData, father_name: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:bg-gray-700 dark:text-white transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      {t(language, 'resources.motherName')}
                    </label>
                    <Input
                      type="text"
                      value={baptismData.mother_name}
                      onChange={(e) => setBaptismData({...baptismData, mother_name: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:bg-gray-700 dark:text-white transition-all"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      {t(language, 'resources.contactEmail')}
                    </label>
                    <Input
                      type="email"
                      value={baptismData.contact_email}
                      onChange={(e) => setBaptismData({...baptismData, contact_email: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:bg-gray-700 dark:text-white transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      {t(language, 'resources.contactPhone')}
                    </label>
                    <Input
                      type="tel"
                      value={baptismData.contact_phone}
                      onChange={(e) => setBaptismData({...baptismData, contact_phone: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:bg-gray-700 dark:text-white transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                    {t(language, 'resources.preferredBaptismDate')}
                  </label>
                  <Input
                    type="date"
                    value={baptismData.preferred_date}
                    onChange={(e) => setBaptismData({...baptismData, preferred_date: e.target.value})}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:bg-gray-700 dark:text-white transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                    {t(language, 'resources.additionalNotes')}
                  </label>
                  <textarea
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:bg-gray-700 dark:text-white transition-all resize-none"
                    rows="4"
                    value={baptismData.additional_notes}
                    onChange={(e) => setBaptismData({...baptismData, additional_notes: e.target.value})}
                    placeholder={t(language, 'resources.additionalNotesPlaceholder')}
                  />
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700">
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? t(language, 'resources.submitting') : t(language, 'resources.submitRequest')}
                </Button>
              </form>
            </Card>
          ) : formType === 'wedding' ? (
            <Card className="p-8 max-w-3xl mx-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {t(language, 'resources.weddingFormTitle')}
                </h3>
                <Button variant="outline" onClick={() => setFormType(null)}>
                  {t(language, 'resources.back')}
                </Button>
              </div>

              <form onSubmit={handleWeddingSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      {t(language, 'resources.groomName')}
                    </label>
                    <Input
                      type="text"
                      value={weddingData.groom_name}
                      onChange={(e) => setWeddingData({...weddingData, groom_name: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 dark:bg-gray-700 dark:text-white transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      {t(language, 'resources.brideName')}
                    </label>
                    <Input
                      type="text"
                      value={weddingData.bride_name}
                      onChange={(e) => setWeddingData({...weddingData, bride_name: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 dark:bg-gray-700 dark:text-white transition-all"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      {t(language, 'resources.contactEmail')}
                    </label>
                    <Input
                      type="email"
                      value={weddingData.contact_email}
                      onChange={(e) => setWeddingData({...weddingData, contact_email: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 dark:bg-gray-700 dark:text-white transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      {t(language, 'resources.contactPhone')}
                    </label>
                    <Input
                      type="tel"
                      value={weddingData.contact_phone}
                      onChange={(e) => setWeddingData({...weddingData, contact_phone: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 dark:bg-gray-700 dark:text-white transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                    {t(language, 'resources.preferredWeddingDate')}
                  </label>
                  <Input
                    type="date"
                    value={weddingData.preferred_date}
                    onChange={(e) => setWeddingData({...weddingData, preferred_date: e.target.value})}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 dark:bg-gray-700 dark:text-white transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                    {t(language, 'resources.additionalNotes')}
                  </label>
                  <textarea
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 dark:bg-gray-700 dark:text-white transition-all resize-none"
                    rows="4"
                    value={weddingData.additional_notes}
                    onChange={(e) => setWeddingData({...weddingData, additional_notes: e.target.value})}
                    placeholder={t(language, 'resources.additionalNotesPlaceholder')}
                  />
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-pink-600 hover:bg-pink-700">
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? t(language, 'resources.submitting') : t(language, 'resources.submitRequest')}
                </Button>
              </form>
            </Card>
          ) : formType === 'counseling' ? (
            <Card className="p-8 max-w-3xl mx-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {t(language, 'resources.counselingFormTitle')}
                </h3>
                <Button variant="outline" onClick={() => setFormType(null)}>
                  {t(language, 'resources.back')}
                </Button>
              </div>

              <form onSubmit={handleCounselingSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                    {t(language, 'resources.fullName')}
                  </label>
                  <Input
                    type="text"
                    value={counselingData.full_name}
                    onChange={(e) => setCounselingData({...counselingData, full_name: e.target.value})}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 dark:bg-gray-700 dark:text-white transition-all"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      {t(language, 'resources.email')}
                    </label>
                    <Input
                      type="email"
                      value={counselingData.email}
                      onChange={(e) => setCounselingData({...counselingData, email: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 dark:bg-gray-700 dark:text-white transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      {t(language, 'resources.phone')}
                    </label>
                    <Input
                      type="tel"
                      value={counselingData.phone}
                      onChange={(e) => setCounselingData({...counselingData, phone: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 dark:bg-gray-700 dark:text-white transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                    {t(language, 'resources.counselingType')}
                  </label>
                  <select
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 dark:bg-gray-700 dark:text-white transition-all"
                    value={counselingData.counseling_type}
                    onChange={(e) => setCounselingData({...counselingData, counseling_type: e.target.value})}
                    required
                  >
                    <option value="">{t(language, 'resources.selectType')}</option>
                    <option value="personal">{t(language, 'resources.personal')}</option>
                    <option value="family">{t(language, 'resources.family')}</option>
                    <option value="youth">{t(language, 'resources.youth')}</option>
                    <option value="other">{t(language, 'resources.other')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                    {t(language, 'resources.messageDetails')}
                  </label>
                  <textarea
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 dark:bg-gray-700 dark:text-white transition-all resize-none"
                    rows="6"
                    value={counselingData.message}
                    onChange={(e) => setCounselingData({...counselingData, message: e.target.value})}
                    required
                    placeholder={t(language, 'resources.counselingPlaceholder')}
                  />
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-green-600 hover:bg-green-700">
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? t(language, 'resources.submitting') : t(language, 'resources.submitRequest')}
                </Button>
              </form>
            </Card>
          ) : formType === 'membership' ? (
            <Card className="p-8 max-w-3xl mx-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {t(language, 'resources.membershipFormTitle')}
                </h3>
                <Button variant="outline" onClick={() => setFormType(null)}>
                  {t(language, 'resources.back')}
                </Button>
              </div>

              <form onSubmit={handleMembershipSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                    {t(language, 'resources.fullName')}
                  </label>
                  <Input
                    type="text"
                    value={membershipData.full_name}
                    onChange={(e) => setMembershipData({...membershipData, full_name: e.target.value})}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:bg-gray-700 dark:text-white transition-all"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      {t(language, 'resources.email')}
                    </label>
                    <Input
                      type="email"
                      value={membershipData.email}
                      onChange={(e) => setMembershipData({...membershipData, email: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:bg-gray-700 dark:text-white transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      {t(language, 'resources.phone')}
                    </label>
                    <Input
                      type="tel"
                      value={membershipData.phone}
                      onChange={(e) => setMembershipData({...membershipData, phone: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:bg-gray-700 dark:text-white transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                    {t(language, 'resources.address')}
                  </label>
                  <Input
                    type="text"
                    value={membershipData.address}
                    onChange={(e) => setMembershipData({...membershipData, address: e.target.value})}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:bg-gray-700 dark:text-white transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                    {t(language, 'resources.whyJoin')}
                  </label>
                  <textarea
                    rows="6"
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:bg-gray-700 dark:text-white transition-all resize-none"
                    value={membershipData.reason}
                    onChange={(e) => setMembershipData({...membershipData, reason: e.target.value})}
                    required
                    placeholder={t(language, 'resources.reasonPlaceholder')}
                  />
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-purple-600 hover:bg-purple-700">
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? t(language, 'resources.submitting') : t(language, 'resources.submitRequest')}
                </Button>
              </form>
            </Card>
          ) : null}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12" style={{fontFamily: 'Playfair Display, serif'}}>
            {t(language, 'resources.faqTitle')}
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq) => (
              <Card key={faq.id} className="overflow-hidden">
                <button
                  onClick={() => toggleFaq(faq.id)}
                  className="w-full px-6 py-4 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <span className="font-semibold text-left text-gray-900 dark:text-white">
                    {language === 'en' ? faq.question_en : faq.question_ti}
                  </span>
                  {openFaq === faq.id ? (
                    <ChevronUp className="w-5 h-5 text-purple-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-purple-600" />
                  )}
                </button>
                {openFaq === faq.id && (
                  <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-gray-700 dark:text-gray-300">
                      {language === 'en' ? faq.answer_en : faq.answer_ti}
                    </p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Resources;
